import { useCallback, useEffect, useState } from "react";
import { FileSignature, Check, X, Download, Inbox } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Modal } from "@/components/ui/Modal";
import { Input } from "@/components/ui/Input";
import { useToast } from "@/hooks/useToast";
import { formatDate } from "@/lib/utils";
import {
  declineSignatureRequest,
  downloadDocument,
  listMyPendingSignatures,
  markSignatureViewed,
  signSignatureRequest,
} from "@/api/documents";
import type { SignatureRequest } from "@/types/document";

export function MySignaturesPanel() {
  const { addToast } = useToast();
  const [requests, setRequests] = useState<SignatureRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [declining, setDeclining] = useState<SignatureRequest | null>(null);
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await listMyPendingSignatures();
      setRequests(data);
    } catch {
      addToast("error", "Failed to load signatures");
    } finally {
      setIsLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    load();
  }, [load]);

  const view = async (r: SignatureRequest) => {
    try {
      await markSignatureViewed(r.id);
      await downloadDocument(r.document_id, r.document_name);
      await load();
    } catch {
      addToast("error", "Failed to open document");
    }
  };

  const sign = async (r: SignatureRequest) => {
    try {
      await signSignatureRequest(r.id);
      addToast("success", `Signed "${r.document_name ?? "document"}"`);
      await load();
    } catch {
      addToast("error", "Failed to sign");
    }
  };

  const decline = async () => {
    if (!declining) return;
    try {
      await declineSignatureRequest(declining.id, reason || undefined);
      addToast("success", "Declined");
      setDeclining(null);
      setReason("");
      await load();
    } catch {
      addToast("error", "Failed to decline");
    }
  };

  if (isLoading) {
    return (
      <Card>
        <p className="text-sm text-text-muted py-6 text-center">Loading…</p>
      </Card>
    );
  }

  if (requests.length === 0) {
    return (
      <Card>
        <div className="py-8 text-center">
          <Inbox className="h-8 w-8 mx-auto mb-2 opacity-40 text-text-muted" />
          <p className="text-sm text-text-muted">Nothing to sign.</p>
        </div>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-3">
        {requests.map((r) => (
          <Card key={r.id} className="flex items-center gap-4">
            <div className="rounded-xl bg-accent/10 p-2.5">
              <FileSignature className="h-5 w-5 text-accent" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <p className="font-medium text-text-primary truncate">
                  {r.document_name ?? "Document"}
                </p>
                <Badge
                  variant={r.status === "VIEWED" ? "info" : "warning"}
                >
                  {r.status}
                </Badge>
              </div>
              <div className="flex items-center gap-3 text-xs text-text-muted">
                <span>Requested {formatDate(r.created_at, "MMM d, yyyy")}</span>
                {r.expires_at && (
                  <span>expires {formatDate(r.expires_at, "MMM d")}</span>
                )}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button variant="ghost" size="sm" onClick={() => view(r)}>
                <Download className="h-4 w-4" />
                Open
              </Button>
              <Button size="sm" onClick={() => sign(r)}>
                <Check className="h-4 w-4" />
                Sign
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDeclining(r)}
              >
                <X className="h-4 w-4" />
                Decline
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <Modal
        isOpen={!!declining}
        onClose={() => setDeclining(null)}
        title="Decline signature"
      >
        <div className="space-y-3">
          <p className="text-sm text-text-muted">
            Let HR know why you're declining "{declining?.document_name}".
          </p>
          <Input
            label="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="I'd like to review the terms first"
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setDeclining(null)}>
              Cancel
            </Button>
            <Button onClick={decline}>Submit decline</Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
